import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import api from '../services/api';
import { setBadgeAccount, clearSignedOutBadge } from '../components/appIconBadge';
import { automaticLoginBlocked, allowAutomaticLogin, blockAutomaticLogin } from './loginPreferences';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const authGeneration = useRef(0);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState(null);

  const checkSession = useCallback(async () => {
    if (automaticLoginBlocked()) { setUser(null); setLoading(false); return; }
    const generation = authGeneration.current;
    try {
      const res = await api.get('/api/auth/me');
      if (generation === authGeneration.current && !automaticLoginBlocked()) {
        setUser(res.data.user);
        if (res.data.user?.role === 'artist') {
          api.get('/api/auth/impersonation/status').then(r => {
            if (generation === authGeneration.current) setImpersonation(r.data?.active ? r.data : null);
          }).catch(() => setImpersonation(null));
        } else setImpersonation(null);
      }
    } catch {
      if (generation === authGeneration.current) setUser(null);
    } finally {
      if (generation === authGeneration.current) setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password, { automatic = false } = {}) => {
    if (automatic && automaticLoginBlocked()) throw new Error("Automatic login is blocked after logout");
    const generation = ++authGeneration.current;
    const res = await api.post('/api/auth/login', { email, password });
    if (generation !== authGeneration.current || (automatic && automaticLoginBlocked())) throw new Error('Login superseded by logout');
    if (!automatic) allowAutomaticLogin();
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    clearSignedOutBadge();
    authGeneration.current += 1;
    blockAutomaticLogin();
    setUser(null);
    setImpersonation(null);
    setLoading(false);
    try {
      const registration = await navigator.serviceWorker?.getRegistration('/');
      const subscription = await registration?.pushManager?.getSubscription();
      if (subscription) {
        await api.delete('/api/chat/push/subscription', { data: { endpoint: subscription.endpoint } });
        await subscription.unsubscribe();
      }
    } catch (_) { /* Logout still completes if push cleanup is unavailable. */ }
    await api.post('/api/auth/logout');
  }, []);

  useEffect(() => {
    if (!loading) {
      if (user?.id) setBadgeAccount(user.id);
      else clearSignedOutBadge();
    }
  }, [user?.id, loading]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const returnToOwner = async () => {
    await api.post('/api/auth/impersonation/return');
    window.location.assign('/admin');
  };
  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkSession }}>
      {impersonation?.active && <div id="aeonas-test-mode-banner" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2147483000, background: '#24554e', color: '#fff', padding: 'max(8px, env(safe-area-inset-top)) 12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12, boxShadow: '0 2px 12px #0005' }}>
        <span>TEST MODE · Logged in as {impersonation.artistName}</span>
        <button type="button" onClick={returnToOwner} style={{ border: '1px solid #a5e9d9', borderRadius: 8, background: '#a5e9d9', color: '#13342e', padding: '8px 10px', fontWeight: 700, flexShrink: 0 }}>Return to Owner</button>
      </div>}
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
